// src/features/MyGymTV/UK/ReviewD6/ReviewD6.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout/Layout';
import Card from 'react-bootstrap/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faPlus, faPlay } from '@fortawesome/free-solid-svg-icons';
import { Button, Alert, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import './review-d6.scss';

const ReviewD6 = () => {
    const navigate = useNavigate();
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const [content, setContent] = useState([]);
    const [mediaUrls, setMediaUrls] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [filenameFilter, setFilenameFilter] = useState('');
    const videoRefs = useRef({});

    useEffect(() => {
        if (selectedClubs.length === 0) {
            setLoading(false);
            return;
        }

        fetchD6Content();

        return () => {
            Object.values(videoRefs.current).forEach(video => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
        };
    }, [selectedClubs]);

    const fetchD6Content = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const clubIds = selectedClubs.map(c => c.ClubId);

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/GetD6Content`,
                { clubIds },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const deduplicatedContent = deduplicateContentByOrderId(response.data);
            setContent(deduplicatedContent);

            deduplicatedContent.forEach(item => {
                if (item.ContentID) {
                    fetchMediaUrl(item.ContentID);
                }
            });

        } catch (err) {
            console.error('Error fetching D6 content:', err);
            setError('Failed to load content');
        } finally {
            setLoading(false);
        }
    };

    const deduplicateContentByOrderId = (contentArray) => {
        const orderMap = new Map();

        contentArray.forEach(item => {
            if (orderMap.has(item.OrderID)) {
                orderMap.get(item.OrderID).venues.push({
                    VenueID: item.VenueID,
                    VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
                });
            } else {
                orderMap.set(item.OrderID, {
                    ...item,
                    venues: [{
                        VenueID: item.VenueID,
                        VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
                    }]
                });
            }
        });

        return Array.from(orderMap.values());
    };

    const fetchMediaUrl = async (contentID) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentID}/content`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            setMediaUrls(prev => ({
                ...prev,
                [contentID]: {
                    url: url,
                    type: blob.type
                }
            }));
        } catch (error) {
            console.error('Error fetching media:', error);
        }
    };

    const handleVideoClick = (contentID, e) => {
        e.stopPropagation();

        const video = videoRefs.current[contentID];
        if (!video) return;

        if (playingVideo && playingVideo !== contentID) {
            const previousVideo = videoRefs.current[playingVideo];
            if (previousVideo) {
                previousVideo.pause();
                previousVideo.currentTime = 0;
            }
        }

        if (video.paused) {
            video.play();
            setPlayingVideo(contentID);
        } else {
            video.pause();
            setPlayingVideo(null);
        }
    };

    const displayMedia = (mediaData, contentID) => {
        if (!mediaData || !mediaData.url) {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <FontAwesomeIcon
                        icon={faSpinner}
                        size="2x"
                        spin
                    />
                </div>
            );
        }

        const isVideo = mediaData.type && mediaData.type.startsWith('video/');

        if (isVideo) {
            const isPlaying = playingVideo === contentID;

            return (
                <div
                    onClick={(e) => handleVideoClick(contentID, e)}
                    style={{ position: 'relative', width: '100%', height: 'auto', cursor: 'pointer' }}
                >
                    <video
                        loop
                        muted
                        onEnded={() => setPlayingVideo(null)}
                        playsInline
                        ref={(el) => videoRefs.current[contentID] = el}
                        style={{ maxHeight: 'auto', width: '100%', display: 'block' }}
                    >
                        <source
                            src={mediaData.url}
                            type={mediaData.type}
                        />
                        Your browser does not support the video tag.
                    </video>
                    {!isPlaying && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                fontSize: '48px',
                                color: 'white',
                                textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                                pointerEvents: 'none',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                borderRadius: '50%',
                                width: '80px',
                                height: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FontAwesomeIcon icon={faPlay} />
                        </div>
                    )}
                </div>
            );
        }

        return (
            <img
                alt="D6 content"
                src={mediaData.url}
                style={{ maxHeight: 'auto', width: '100%' }}
            />
        );
    };

    const filterContentByFilename = (contentArray) => {
        if (!filenameFilter.trim()) {
            return contentArray;
        }

        return contentArray.filter(item =>
            item.Filename &&
            item.Filename.toLowerCase().includes(filenameFilter.toLowerCase())
        );
    };

    const categorizeContent = () => {
        const today = moment().startOf('day');

        const nowPlaying = [];
        const scheduled = [];
        const expired = [];

        const filteredContent = filterContentByFilename(content);

        filteredContent.forEach(item => {
            const startDate = item.StartDate ? moment(item.StartDate) : null;
            const endDate = item.EndDate ? moment(item.EndDate) : null;

            if (
                item.IsActive &&
                (!startDate || startDate.isSameOrBefore(today)) &&
                (!endDate || endDate.year() >= 2100 || endDate.isAfter(today))
            ) {
                nowPlaying.push(item);
            } else if (
                item.IsActive &&
                startDate && startDate.isAfter(today)
            ) {
                scheduled.push(item);
            } else if (
                !item.IsActive || (endDate && endDate.year() < 2100 && endDate.isBefore(today))
            ) {
                expired.push(item);
            }
        });

        return { nowPlaying, scheduled, expired };
    };

    const { nowPlaying, scheduled, expired } = categorizeContent();

    if (selectedClubs.length === 0) {
        return (
            <Layout>
                <div className="review-d6-container mt-5">
                    <Alert variant="warning">
                        Please select one or more venues from the menu to review content.
                    </Alert>
                </div>
            </Layout>
        );
    }

    if (loading) {
        return (
            <Layout>
                <div className="review-d6-container mt-5 text-center">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        size="3x"
                        spin
                    />
                    <p className="mt-3">Loading content...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="review-d6-container mt-5">
                    <Alert variant="danger">{error}</Alert>
                </div>
            </Layout>
        );
    }

    if (content.length === 0) {
        return (
            <Layout>
                <div className="review-d6-container mt-5">
                    <Alert variant="info">
                        No D6 content found for the selected venues.
                    </Alert>
                    <Button onClick={() => navigate('/uk/add-d6')}>
                        Add D6 Content
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="review-d6-container">
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h4>Review D6 Content</h4>
                            <small className="text-muted">
                                Viewing content for {selectedClubs.length} selected venue(s)
                            </small>
                        </div>
                        <Button
                            onClick={() => navigate('/uk/add-d6')}
                            variant="primary"
                        >
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faPlus}
                            />
                            Add D6 Content
                        </Button>
                    </div>

                    {content.length > 0 && (
                        <div className="d-flex align-items-center gap-3">
                            <Form.Control
                                onChange={(e) => setFilenameFilter(e.target.value)}
                                placeholder="Filter by filename..."
                                style={{ maxWidth: '400px' }}
                                type="text"
                                value={filenameFilter}
                            />
                            {filenameFilter && (
                                <Button
                                    onClick={() => setFilenameFilter('')}
                                    size="sm"
                                    variant="outline-secondary"
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                <Card className="border-0">
                    {nowPlaying.length > 0 && (
                        <div className="content-section">
                            <Card.Header className="text-end">
                                Now Playing ({nowPlaying.length})
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {nowPlaying.map((item) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={item.OrderID}
                                        >
                                            <div className="now-playing-tag d-flex justify-content-between align-items-center w-100">
                                                {item.EndDate && moment(item.EndDate).year() < 2100 ? (
                                                    <span>
                                                        Now playing through{' '}
                                                        {moment(item.EndDate).format('DD/MM/YYYY')}
                                                    </span>
                                                ) : (
                                                    <span>Now playing ∞</span>
                                                )}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigate(`/uk/d6-details/${item.OrderContentID}`)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div className="content-image-wrapper">
                                                {displayMedia(mediaUrls[item.ContentID], item.ContentID)}
                                                {item.venues.length > 1 && (
                                                    <div className="venue-count-badge">
                                                        {item.venues.length}
                                                    </div>
                                                )}
                                            </div>
                                            {item.Filename && (
                                                <div
                                                    className="mt-2 text-muted small"
                                                    style={{ wordBreak: 'break-word' }}
                                                >
                                                    {item.Filename}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                    )}

                    {scheduled.length > 0 && (
                        <div className="content-section">
                            <Card.Header className="text-end">
                                Coming Soon ({scheduled.length})
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {scheduled.map((item) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={item.OrderID}
                                        >
                                            <div className="scheduled-tag d-flex justify-content-between align-items-center w-100">
                                                Starting {moment(item.StartDate).format('DD/MM/YYYY')}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigate(`/uk/d6-details/${item.OrderContentID}`)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div className="content-image-wrapper">
                                                {displayMedia(mediaUrls[item.ContentID], item.ContentID)}
                                                {item.venues.length > 1 && (
                                                    <div className="venue-count-badge">
                                                        {item.venues.length}
                                                    </div>
                                                )}
                                            </div>
                                            {item.Filename && (
                                                <div
                                                    className="mt-2 text-muted small"
                                                    style={{ wordBreak: 'break-word' }}
                                                >
                                                    {item.Filename}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                    )}

                    {expired.length > 0 && (
                        <div className="content-section">
                            <Card.Header className="text-end">
                                Inactive ({expired.length})
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {expired.map((item) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={item.OrderID}
                                        >
                                            <div className="expired-tag d-flex justify-content-between align-items-center w-100">
                                                Inactive
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigate(`/uk/d6-details/${item.OrderContentID}`)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div className="content-image-wrapper">
                                                {displayMedia(mediaUrls[item.ContentID], item.ContentID)}
                                                {item.venues.length > 1 && (
                                                    <div className="venue-count-badge">
                                                        {item.venues.length}
                                                    </div>
                                                )}
                                            </div>
                                            {item.Filename && (
                                                <div
                                                    className="mt-2 text-muted small"
                                                    style={{ wordBreak: 'break-word' }}
                                                >
                                                    {item.Filename}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                    )}

                    {filenameFilter && nowPlaying.length === 0 && scheduled.length === 0 && expired.length === 0 && (
                        <Alert
                            className="m-3"
                            variant="info"
                        >
                            No content found matching "{filenameFilter}"
                        </Alert>
                    )}
                </Card>
            </div>
        </Layout>
    );
};

export default ReviewD6;